import React, { useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { OrderService, ProjectSelectionMode, PublicCatalogCategoryData } from '../../api';
import RotatingLoader from '../../ui/RotatingLoader';
import { ProjectSelectionPicker } from '../../projectSelection/ProjectSelectionPicker';
import {
  getProjectSelectionConfig,
  getRequiredProjectQuantity,
  getSelectedProjectQuantity,
} from '../../projectSelection/projectSelectionUtils';
import { OrderPageTypography } from '../themedComponents/OrderPageTypography';
import { useOrderPage } from '../orderPageContext/useOrderPage';

type ProjectSelectionStepProps = {
  setShouldDisableNext: (value: boolean) => void;
};

export const ProjectSelectionStep = ({ setShouldDisableNext }: ProjectSelectionStepProps) => {
  const { t } = useTranslation();
  const { orderPageState, setOrderPageState } = useOrderPage();
  const [categories, setCategories] = useState<PublicCatalogCategoryData[] | undefined>(undefined);

  const projectSelectionConfig = getProjectSelectionConfig(orderPageState.orderPage);
  const isRequired = projectSelectionConfig?.mode === ProjectSelectionMode.IN_BOOKING_REQUIRED;
  const requiredQuantity = getRequiredProjectQuantity(
    projectSelectionConfig?.selection_count,
    orderPageState.details.persons,
  );
  const selection = orderPageState.projectSelection || [];
  const selectedQuantity = getSelectedProjectQuantity(selection);
  const selectionIsValid = isRequired
    ? selectedQuantity === requiredQuantity
    : selectedQuantity === 0 || selectedQuantity === requiredQuantity;

  useEffect(() => {
    void (async () => {
      const catalogCategories = await OrderService.getProjectSelectionCatalog(
        orderPageState.account.id,
        orderPageState.orderPage.id,
      );
      setCategories(catalogCategories);
    })();
  }, []);

  useEffect(() => {
    setShouldDisableNext(!selectionIsValid);
    return () => setShouldDisableNext(false);
  }, [selectionIsValid, setShouldDisableNext]);

  if (!categories) {
    return (
      <Grid container justifyContent="center" mt={4}>
        <RotatingLoader />
      </Grid>
    );
  }

  return (
    <Grid container flexDirection="column" gap={2} mt={2}>
      <OrderPageTypography variant="h3" bold>
        {t('project_selection.title')}
      </OrderPageTypography>
      {!isRequired && <OrderPageTypography variant="body2">{t('project_selection.optional_hint')}</OrderPageTypography>}
      <ProjectSelectionPicker
        categories={categories}
        selectionCount={projectSelectionConfig?.selection_count}
        requiredQuantity={requiredQuantity}
        currency={orderPageState.account.currency}
        selection={selection}
        onChange={(newSelection) => setOrderPageState({ ...orderPageState, projectSelection: newSelection })}
      />
    </Grid>
  );
};

export default ProjectSelectionStep;
